import { useState } from "react";

import { ApiClientError } from "../api/client.js";
import { useStore } from "../store.js";

const inputClass =
  "w-full rounded-lg border border-node-border bg-canvas px-3 py-2 text-sm text-ink outline-none focus:border-accent";

interface Props {
  onClose: () => void;
}

export function SettingsPanel({ onClose }: Props) {
  const user = useStore((state) => state.user);
  const updateEmail = useStore((state) => state.updateEmail);
  const changePassword = useStore((state) => state.changePassword);

  const [email, setEmail] = useState(user?.email ?? "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>, success: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage({ kind: "ok", text: success });
    } catch (caught) {
      const text = caught instanceof ApiClientError ? caught.message : "Something went wrong.";
      setMessage({ kind: "error", text });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-node-border bg-node-bg p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Account</h2>
          <button type="button" onClick={onClose} className="text-sm text-ink-muted hover:text-ink">
            Close
          </button>
        </div>

        {message ? (
          <p
            className={`mb-4 rounded-lg px-3 py-2 text-sm ${
              message.kind === "ok"
                ? "border border-positive/40 bg-positive/10 text-ink"
                : "border border-warning/40 bg-warning/10 text-ink"
            }`}
          >
            {message.text}
          </p>
        ) : null}

        <form
          className="mb-6"
          onSubmit={(event) => {
            event.preventDefault();
            void run(() => updateEmail(email), "Email updated.");
          }}
        >
          <label className="mb-1 block text-xs text-ink-muted" htmlFor="settings-email">
            Email
          </label>
          <div className="flex gap-2">
            <input
              id="settings-email"
              type="email"
              className={inputClass}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="shrink-0 rounded-lg border border-node-border px-3 text-sm text-ink hover:border-accent disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </form>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await changePassword(currentPassword, newPassword);
              setCurrentPassword("");
              setNewPassword("");
            }, "Password changed.");
          }}
        >
          <p className="mb-2 text-sm font-medium text-ink">Change password</p>
          <label className="mb-1 block text-xs text-ink-muted" htmlFor="settings-current">
            Current password
          </label>
          <input
            id="settings-current"
            type="password"
            autoComplete="current-password"
            className={`${inputClass} mb-3`}
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            required
          />
          <label className="mb-1 block text-xs text-ink-muted" htmlFor="settings-new">
            New password
          </label>
          <input
            id="settings-new"
            type="password"
            autoComplete="new-password"
            minLength={8}
            className={`${inputClass} mb-4`}
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
          />
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-lg bg-accent px-4 py-2 text-sm font-medium text-canvas hover:opacity-90 disabled:opacity-60"
          >
            Update password
          </button>
        </form>
      </div>
    </div>
  );
}
